import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { showCreateListingLinkForUser } from '../../util/userHelpers';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { selectFavorites } from '../../ducks/favorites.duck';

import {
  H3,
  Page,
  ListingCard,
  NamedLink,
  UserNav,
  LayoutSingleColumn,
} from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import { getFavoriteListings } from './FavoriteListingsPage.duck';
import css from './FavoriteListingsPage.module.css';

const Heading = props => {
  const { listingsAreLoaded, hasFavorites, count } = props;

  return listingsAreLoaded && hasFavorites ? (
    <H3 as="h1" className={css.heading}>
      <FormattedMessage id="FavoriteListingsPage.youHaveFavorites" values={{ count }} />
    </H3>
  ) : listingsAreLoaded && !hasFavorites ? (
    <div className={css.noResultsContainer}>
      <H3 as="h1" className={css.headingNoListings}>
        <FormattedMessage id="FavoriteListingsPage.noFavorites" />
      </H3>
      <p className={css.createListingParagraph}>
        <NamedLink className={css.createListingLink} name="SearchPage">
          <FormattedMessage id="FavoriteListingsPage.browseListings" />
        </NamedLink>
      </p>
    </div>
  ) : null;
};

/**
 * Favorites page — renders the listings the current user has hearted.
 * Shares the chrome (UserNav + heading + grid) with ManageListingsPage
 * so authenticated dashboards stay visually consistent.
 *
 * @param {Object} props
 * @param {propTypes.currentUser} props.currentUser
 * @param {propTypes.listing[]} props.listings
 * @param {string[]} props.favoriteIds
 * @param {boolean} props.queryInProgress
 * @param {propTypes.error} props.queryListingsError
 * @param {boolean} props.scrollingDisabled
 */
export const FavoriteListingsPageComponent = props => {
  const intl = useIntl();
  const config = useConfiguration();

  const {
    currentUser,
    listings = [],
    favoriteIds = [],
    queryInProgress,
    queryListingsError,
    scrollingDisabled,
  } = props;

  const hasFavorites = favoriteIds.length > 0;
  const listingsAreLoaded = !queryInProgress;

  const loadingResults = (
    <div className={css.messagePanel}>
      <H3 as="h2" className={css.heading}>
        <FormattedMessage id="FavoriteListingsPage.loadingFavorites" />
      </H3>
    </div>
  );

  const queryError = (
    <div className={css.messagePanel}>
      <H3 as="h2" className={css.heading}>
        <FormattedMessage id="FavoriteListingsPage.queryError" />
      </H3>
    </div>
  );

  const showManageListingsLink = showCreateListingLinkForUser(config, currentUser);

  return (
    <Page
      title={intl.formatMessage({ id: 'FavoriteListingsPage.title' })}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn
        topbar={
          <>
            <TopbarContainer currentPage="FavoriteListingsPage" />
            {/* UserNav only makes sense for signed-in users — its
                tabs all point to auth-gated pages. Guests still get
                the favorites page itself (favorites cached in
                localStorage), but skip the dashboard tab strip. */}
            {currentUser ? (
              <UserNav
                currentPage="FavoriteListingsPage"
                showManageListingsLink={showManageListingsLink}
              />
            ) : null}
          </>
        }
        footer={<FooterContainer />}
      >
        {queryInProgress ? loadingResults : null}
        {queryListingsError ? queryError : null}

        <div className={css.listingPanel}>
          <Heading
            listingsAreLoaded={listingsAreLoaded}
            hasFavorites={hasFavorites}
            count={favoriteIds.length}
          />

          {listingsAreLoaded && hasFavorites ? (
            <ul className={css.listingCards}>
              {listings.map(l => (
                <li key={l.id.uuid} className={css.listingCard}>
                  <ListingCard listing={l} />
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const { queryInProgress, queryListingsError } = state.FavoriteListingsPage;
  return {
    currentUser,
    listings: getFavoriteListings(state),
    favoriteIds: selectFavorites(state),
    queryInProgress,
    queryListingsError,
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const FavoriteListingsPage = compose(connect(mapStateToProps))(FavoriteListingsPageComponent);

export default FavoriteListingsPage;
